import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Check, CircleDot, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = `${API}/api/admin/commerciale`;
const EMPTY_ACTIVITY = { prospect_id: "", tipo: "Chiamata", testo: "", data_prevista: "", commerciale: "", commerciale_id: "" };

function getError(json, fallback) {
  return typeof json?.detail === "string" ? json.detail : fallback;
}

function isCompleted(activity) {
  return ["completata", "completato", "chiusa", "chiuso"].includes(String(activity.stato || "").toLowerCase());
}

function renderDate(value) {
  if (!value) return "Non pianificata";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
}

export default function AttivitaAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [activities, setActivities] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [filter, setFilter] = useState("aperte");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ACTIVITY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "tutte") params.set("stato", filter);
      const [activitiesResponse, prospectsResponse, operatorsResponse] = await Promise.all([
        fetch(`${BASE_URL}/attivita?${params.toString()}`, { headers }),
        fetch(`${BASE_URL}/prospects`, { headers }),
        fetch(`${BASE_URL}/operatori`, { headers }),
      ]);
      const [activitiesJson, prospectsJson, operatorsJson] = await Promise.all([
        activitiesResponse.json().catch(() => ({})),
        prospectsResponse.json().catch(() => ({})),
        operatorsResponse.json().catch(() => ({})),
      ]);
      if (!activitiesResponse.ok) throw new Error(getError(activitiesJson, "Impossibile caricare le attività"));
      if (!prospectsResponse.ok) throw new Error(getError(prospectsJson, "Impossibile caricare i prospect"));
      if (!operatorsResponse.ok) throw new Error(getError(operatorsJson, "Impossibile caricare gli operatori"));
      setActivities(activitiesJson.data || []);
      setProspects(prospectsJson.data || []);
      setOperatori(operatorsJson.data || []);
    } catch (error) {
      toast.error(error.message || "Errore nel caricamento delle attività");
    } finally {
      setLoading(false);
    }
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.prospect_id || !form.testo.trim()) {
      toast.error("Seleziona un prospect e inserisci il testo dell'attività.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, prospect_id: Number(form.prospect_id) || form.prospect_id };
      const res = await fetch(`${BASE_URL}/attivita`, { method: "POST", headers, body: JSON.stringify(payload) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(getError(json, "Creazione attività non riuscita"));
      toast.success("Attività creata");
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.message || "Errore durante la creazione dell'attività");
    } finally {
      setSaving(false);
    }
  };

  const complete = async (activity) => {
    try {
      const res = await fetch(`${BASE_URL}/attivita/${activity.id}`, {
        method: "PATCH", headers, body: JSON.stringify({ stato: "completata", esito: activity.esito || "Completata" }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(getError(json, "Completamento attività non riuscito"));
      setActivities((current) => current.map((item) => item.id === activity.id ? { ...item, stato: "completata", esito: activity.esito || "Completata" } : item));
      toast.success("Attività completata");
      if (filter === "aperte") load();
    } catch (error) {
      toast.error(error.message || "Errore durante il completamento");
    }
  };

  const remove = async (activity) => {
    if (!window.confirm("Eliminare questa attività?")) return;
    try {
      const res = await fetch(`${BASE_URL}/attivita/${activity.id}`, { method: "DELETE", headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(getError(json, "Eliminazione attività non riuscita"));
      setActivities((current) => current.filter((item) => item.id !== activity.id));
      toast.success("Attività eliminata");
    } catch (error) {
      toast.error(error.message || "Errore durante l'eliminazione");
    }
  };

  const nameFor = (activity) => activity.prospect?.nome_struttura || activity.nome_struttura || prospects.find((item) => String(item.id) === String(activity.prospect_id))?.nome_struttura || "Prospect non disponibile";

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={CalendarClock}
        title="Attività commerciali"
        description="Promemoria e attività dei prospect assegnati."
        actions={<><Button size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna</Button><Button size="sm" onClick={() => { setForm(EMPTY_ACTIVITY); setDialogOpen(true); }}><Plus className="mr-1 h-4 w-4" /> Nuova attività</Button></>}
      />
      <div className="w-48"><Label className="text-xs">Mostra</Label><Select value={filter} onValueChange={setFilter}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="aperte">Aperte</SelectItem><SelectItem value="completate">Completate</SelectItem><SelectItem value="tutte">Tutte</SelectItem></SelectContent></Select></div>
      {loading ? <div className="py-16 text-center text-slate-500"><Loader2 className="mr-2 inline h-5 w-5 animate-spin" />Caricamento attività…</div>
        : !activities.length ? <Card><CardContent className="p-10 text-center text-slate-500">Nessuna attività da mostrare.</CardContent></Card>
          : <div className="space-y-2">{activities.map((activity) => {
            const completed = isCompleted(activity);
            return <Card key={activity.id}><CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
              <div className={`rounded-full p-2 ${completed ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{completed ? <Check className="h-5 w-5" /> : <CircleDot className="h-5 w-5" />}</div>
              <div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><p className="font-medium">{activity.testo}</p><Badge variant={completed ? "default" : "secondary"}>{activity.stato || "aperta"}</Badge><Badge variant="outline">{activity.tipo || "Attività"}</Badge></div><p className="mt-1 text-sm text-slate-600">{nameFor(activity)} · {activity.commerciale || "Non assegnata"}</p><p className="mt-1 text-xs text-slate-500">Prevista: {renderDate(activity.data_prevista)}{activity.esito ? ` · Esito: ${activity.esito}` : ""}</p></div>
              <div className="flex shrink-0 gap-1">{!completed && <Button size="sm" variant="outline" onClick={() => complete(activity)}><Check className="mr-1 h-4 w-4" /> Completa</Button>}<Button size="icon" variant="ghost" onClick={() => remove(activity)} title="Elimina attività"><Trash2 className="h-4 w-4 text-rose-600" /></Button></div>
            </CardContent></Card>;
          })}</div>}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogContent><DialogHeader><DialogTitle>Nuova attività</DialogTitle></DialogHeader>
        <div className="space-y-3"><div><Label>Prospect *</Label><Select value={form.prospect_id} onValueChange={(value) => setForm((current) => ({ ...current, prospect_id: value }))}><SelectTrigger><SelectValue placeholder="Seleziona struttura" /></SelectTrigger><SelectContent>{prospects.map((prospect) => <SelectItem key={prospect.id} value={String(prospect.id)}>{prospect.nome_struttura}</SelectItem>)}</SelectContent></Select></div><div><Label>Tipo</Label><Select value={form.tipo} onValueChange={(value) => setForm((current) => ({ ...current, tipo: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Chiamata", "Email", "Appuntamento", "Follow-up", "Altro"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></div><div><Label>Testo *</Label><Textarea value={form.testo} onChange={(event) => setForm((current) => ({ ...current, testo: event.target.value }))} /></div><div><Label>Data prevista</Label><Input type="datetime-local" value={form.data_prevista} onChange={(event) => setForm((current) => ({ ...current, data_prevista: event.target.value }))} /></div><div><Label>Commerciale</Label><Select value={form.commerciale || "__none__"} onValueChange={(value) => { const operator = operatori.find((item) => item.email === value); setForm((current) => ({ ...current, commerciale: value === "__none__" ? "" : value, commerciale_id: operator?.id || "" })); }}><SelectTrigger><SelectValue placeholder="Non assegnato" /></SelectTrigger><SelectContent><SelectItem value="__none__">Non assegnato</SelectItem>{operatori.map((operator) => <SelectItem key={operator.id || operator.email} value={operator.email}>{operator.nome || operator.name || operator.email}</SelectItem>)}</SelectContent></Select></div></div>
        <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button><Button onClick={create} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea attività</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
